import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Upload, X, Award } from 'lucide-react';
import { partnersAPI, uploadAPI } from '@/lib/api';

const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    logo_url: '',
    link_url: '',
    is_large: false,
    order: 0,
    created_at: '',
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await partnersAPI.getAll();
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      // Make sure URL is complete
      let imageUrl = response.data.url;
      if (imageUrl.startsWith('/')) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        imageUrl = `${backendUrl}${imageUrl}`;
      }
      setFormData({ ...formData, logo_url: imageUrl });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('آپلود لوگو با خطا مواجه شد');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingPartner) {
        await partnersAPI.update(formData.id, formData);
        alert('لوگو با موفقیت به‌روزرسانی شد');
      } else {
        const newId = `partner_${Date.now()}`;
        const newPartner = {
          ...formData,
          id: newId,
          created_at: new Date().toISOString(),
          order: partners.length,
        };
        await partnersAPI.create(newPartner);
        alert('لوگو با موفقیت اضافه شد');
      }
      
      fetchPartners();
      closeModal();
    } catch (error) {
      console.error('Failed to save partner:', error);
      alert('خطا در ذخیره لوگو');
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData(partner);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این لوگو اطمینان دارید؟')) return;

    try {
      await partnersAPI.delete(id);
      alert('لوگو با موفقیت حذف شد');
      fetchPartners();
    } catch (error) {
      console.error('Failed to delete partner:', error);
      alert('خطا در حذف لوگو');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPartner(null);
    setFormData({
      id: '',
      name: '',
      logo_url: '',
      link_url: '',
      university_id: '',
      is_large: false,
      order: 0,
      created_at: '',
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-black p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">مدیریت لوگوهای شرکا</h1>
            <p className="text-zinc-400 text-sm">مدیریت لوگوهای دانشگاه‌ها و شرکای معتبر</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors"
          >
            <Plus size={20} />
            افزودن لوگو جدید
          </button>
        </div>

        {/* Partners Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {partners.map((partner) => (
            <div key={partner.id} className="glass-effect border border-white/10 rounded-xl p-4 group relative">
              <div className="relative">
                <img
                  src={partner.logo_url}
                  alt={partner.name}
                  className={`w-full ${partner.is_large ? 'h-32' : 'h-20'} object-contain grayscale group-hover:grayscale-0 transition-all`}
                />
                {partner.is_large && (
                  <div className="absolute top-0 right-0">
                    <Award size={16} className="text-[#D4AF37]" />
                  </div>
                )}
              </div>
              <div className="mt-3">
                <p className="text-white text-sm font-medium text-center line-clamp-1">{partner.name}</p>
              </div>
              
              {/* Actions */}
              <div className="mt-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleEdit(partner)}
                  className="flex-1 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
                >
                  <Edit2 size={14} className="mx-auto" />
                </button>
                <button
                  onClick={() => handleDelete(partner.id)}
                  className="flex-1 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs"
                >
                  <Trash2 size={14} className="mx-auto" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {partners.length === 0 && (
          <div className="text-center text-zinc-400 py-12">
            هنوز لوگویی اضافه نشده است. برای افزودن لوگو جدید روی دکمه بالا کلیک کنید.
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingPartner ? 'ویرایش لوگو' : 'افزودن لوگو جدید'}
                </h2>
                <button onClick={closeModal} className="text-zinc-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">لوگو *</label>
                  {formData.logo_url ? (
                    <div className="relative">
                      <img src={formData.logo_url} alt="Preview" className="w-full h-32 object-contain bg-white rounded-lg p-4" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, logo_url: '' })}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-zinc-700 rounded-lg p-12 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload" className="cursor-pointer">
                        <Upload className="mx-auto mb-2 text-zinc-400" size={48} />
                        <p className="text-sm text-zinc-400">
                          {uploading ? 'در حال آپلود...' : 'کلیک کنید یا لوگو را بکشید'}
                        </p>
                      </label>
                    </div>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">نام شریک *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="مثال: دانشگاه تورنتو"
                    required
                  />
                </div>

                {/* Link URL */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">لینک خارجی (اختیاری)</label>
                  <input
                    type="url"
                    value={formData.link_url}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="https://example.com"
                  />
                  <p className="text-xs text-zinc-500 mt-1">برای لینک‌های خارجی مثل ICEF Agent ID</p>
                </div>

                {/* University ID */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">شناسه دانشگاه/مدرسه (اختیاری)</label>
                  <input
                    type="text"
                    value={formData.university_id}
                    onChange={(e) => setFormData({ ...formData, university_id: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="مثال: uni_123"
                  />
                  <p className="text-xs text-zinc-500 mt-1">برای لینک به صفحه داخلی دانشگاه/مدرسه</p>
                </div>

                {/* Is Large */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_large"
                    checked={formData.is_large}
                    onChange={(e) => setFormData({ ...formData, is_large: e.target.checked })}
                    className="w-5 h-5 rounded border-white/10 bg-black text-[#D4AF37] focus:ring-[#D4AF37]"
                  />
                  <label htmlFor="is_large" className="text-sm text-zinc-300">
                    نمایش بزرگ‌تر (برای لوگوهای مهم مثل ICEF)
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors font-medium"
                  >
                    {editingPartner ? 'به‌روزرسانی' : 'ایجاد'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPartners;
